import type { AdminOrgDto, OrgStatusAction } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CompanyStatusBadge } from './CompanyStatusBadge';
import { SectionCard } from './SectionCard';
import { StatusActionDialog } from './StatusActionDialog';
import { changeOrgStatus } from '../../../api/admin-orgs';
import { formatDate } from '../../../utils/format';
import { useOrgWrite } from '../_lib/useOrgWrite';

/**
 * «Статус» (AUT-403), after `companies-detail.html?tab=status`: the current
 * status, then one card per allowed move — active → «Приостановить»,
 * suspended → «Возобновить», and «Прекратить» (final) from either. A
 * terminated company shows no actions (the API refuses: 409). Each move
 * goes through {@link StatusActionDialog}. The mockup's «История» needs an
 * org audit read the API does not have yet, so it is not rendered.
 */
const POINTS = ['p1', 'p2', 'p3'] as const;

const ActionCard = ({
  action,
  onStart,
}: {
  action: OrgStatusAction;
  onStart: (action: OrgStatusAction) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const ns = `companies.statusTab.${action}`;
  return (
    <SectionCard
      title={t(`${ns}.title`)}
      sub={t(`${ns}.kind`)}
      {...(action === 'terminate' ? { tone: 'danger' as const } : {})}
    >
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {POINTS.map((p) => (
          <Typography key={p} component="li" variant="body2">
            {t(`${ns}.${p}`)}
          </Typography>
        ))}
      </Box>
      <Box>
        <Button
          variant={action === 'reinstate' ? 'contained' : 'outlined'}
          color={action === 'terminate' ? 'error' : action === 'suspend' ? 'warning' : 'primary'}
          onClick={() => onStart(action)}
          data-testid={`company-status-${action}`}
        >
          {t(`${ns}.button`)}
        </Button>
      </Box>
    </SectionCard>
  );
};

const ALLOWED: Record<AdminOrgDto['status'], OrgStatusAction[]> = {
  active: ['suspend', 'terminate'],
  suspended: ['reinstate', 'terminate'],
  terminated: [],
};

export const StatusTab = ({ org }: { org: AdminOrgDto }): ReactElement => {
  const { t } = useTranslation();
  const [action, setAction] = useState<OrgStatusAction | null>(null);
  const write = useOrgWrite();

  const confirm = (next: OrgStatusAction, reason: string): void => {
    void write
      .run(() => changeOrgStatus(org.id, next, reason), `companies.statusDialog.${next}.success`)
      .then((ok) => {
        if (ok) setAction(null);
      });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard title={t('companies.statusTab.current')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CompanyStatusBadge status={org.status} />
          <Typography variant="body2" color="text.secondary">
            {t('companies.statusTab.since', {
              date: formatDate(org.statusChangedAt ?? org.createdAt),
            })}{' '}
            · {t(`companies.statusTab.hint.${org.status}`)}
          </Typography>
        </Box>
      </SectionCard>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        {ALLOWED[org.status].map((a) => (
          <ActionCard key={a} action={a} onStart={setAction} />
        ))}
      </Box>
      <StatusActionDialog
        action={action}
        companyName={org.name}
        activeMembers={org.activeMembers}
        loading={write.busy}
        onConfirm={confirm}
        onCancel={() => setAction(null)}
      />
    </Box>
  );
};
